export type HttpHeaders = Record<string, string | string[]>;

export type HttpHeaderPair = {
  name: string;
  value: string;
};

function getFirstHeaderValue(value: string | string[]): string {
  return Array.isArray(value) ? (value[0] ?? '') : value;
}

function isHostHeader(headerName: string): boolean {
  return headerName.toLowerCase() === 'host';
}

function normalizeHttpHeadersRecord(headers: HttpHeaders): HttpHeaders {
  const normalizedHeaders: HttpHeaders = {};
  let pseudoAuthority: string | undefined;
  let hasHost = false;

  for (const [headerName, headerValue] of Object.entries(headers)) {
    if (isHttpPseudoHeader(headerName)) {
      if (headerName === ':authority' && !pseudoAuthority) {
        const authorityValue = getFirstHeaderValue(headerValue).trim();
        if (authorityValue.length > 0) {
          pseudoAuthority = authorityValue;
        }
      }
      continue;
    }

    if (isHostHeader(headerName)) {
      if (!hasHost) {
        normalizedHeaders[headerName] = headerValue;
        hasHost = true;
      }
      continue;
    }

    normalizedHeaders[headerName] = headerValue;
  }

  if (!hasHost && pseudoAuthority) {
    return {
      Host: pseudoAuthority,
      ...normalizedHeaders,
    };
  }

  return normalizedHeaders;
}

function normalizeHttpHeadersRaw(rawHeaders: string[]): string[] {
  const normalizedRawHeaders: string[] = [];
  let pseudoAuthority: string | undefined;
  let hasHost = false;

  for (let index = 0; index < rawHeaders.length; index += 2) {
    const name = rawHeaders[index];
    const value = rawHeaders[index + 1] ?? '';

    if (isHttpPseudoHeader(name)) {
      if (name === ':authority' && !pseudoAuthority) {
        const authorityValue = value.trim();
        if (authorityValue.length > 0) {
          pseudoAuthority = authorityValue;
        }
      }
      continue;
    }

    if (isHostHeader(name)) {
      if (!hasHost) {
        normalizedRawHeaders.push(name, value);
        hasHost = true;
      }
      continue;
    }

    normalizedRawHeaders.push(name, value);
  }

  if (!hasHost && pseudoAuthority) {
    return ['Host', pseudoAuthority, ...normalizedRawHeaders];
  }

  return normalizedRawHeaders;
}

function normalizeHttpHeadersPairs(
  headers: HttpHeaderPair[],
): HttpHeaderPair[] {
  const normalizedHeaders: HttpHeaderPair[] = [];
  let pseudoAuthority: string | undefined;
  let hasHost = false;

  for (const header of headers) {
    if (isHttpPseudoHeader(header.name)) {
      if (header.name === ':authority' && !pseudoAuthority) {
        const authorityValue = header.value.trim();
        if (authorityValue.length > 0) {
          pseudoAuthority = authorityValue;
        }
      }
      continue;
    }

    if (isHostHeader(header.name)) {
      if (!hasHost) {
        normalizedHeaders.push(header);
        hasHost = true;
      }
      continue;
    }

    normalizedHeaders.push(header);
  }

  if (!hasHost && pseudoAuthority) {
    return [{ name: 'Host', value: pseudoAuthority }, ...normalizedHeaders];
  }

  return normalizedHeaders;
}

export function normalizeHttpHeaders(
  headers: HttpHeaderPair[],
): HttpHeaderPair[];
export function normalizeHttpHeaders(headers: HttpHeaders): HttpHeaders;
export function normalizeHttpHeaders(rawHeaders: string[]): string[];
export function normalizeHttpHeaders(
  headers: HttpHeaderPair[] | HttpHeaders | string[],
): HttpHeaderPair[] | HttpHeaders | string[] {
  if (Array.isArray(headers)) {
    if (headers.length === 0) {
      return [];
    }

    if (typeof headers[0] === 'string') {
      return normalizeHttpHeadersRaw(headers as string[]);
    }

    return normalizeHttpHeadersPairs(headers as HttpHeaderPair[]);
  }

  return normalizeHttpHeadersRecord(headers);
}

export function headersContainPseudoHeader(
  headers?: HttpHeaderPair[] | HttpHeaders | string[],
): boolean {
  if (!headers) {
    return false;
  }

  if (Array.isArray(headers)) {
    if (headers.length === 0) {
      return false;
    }

    if (typeof headers[0] === 'string') {
      const rawHeaders = headers as string[];
      for (let index = 0; index < rawHeaders.length; index += 2) {
        if (isHttpPseudoHeader(rawHeaders[index])) {
          return true;
        }
      }
      return false;
    }

    return (headers as HttpHeaderPair[]).some((header) =>
      isHttpPseudoHeader(header.name),
    );
  }

  return Object.keys(headers).some((headerName) =>
    isHttpPseudoHeader(headerName),
  );
}

export function isHttpPseudoHeader(headerName: string): boolean {
  return headerName.startsWith(':');
}

export function formatDisplayHeaderLines({
  headers,
  rawHeaders,
}: {
  headers?: HttpHeaders;
  rawHeaders?: string[];
}): string[] {
  if (Array.isArray(rawHeaders) && rawHeaders.length > 0) {
    const normalizedRawHeaders = normalizeHttpHeaders(rawHeaders);
    const lines: string[] = [];

    for (let index = 0; index < normalizedRawHeaders.length; index += 2) {
      const name = normalizedRawHeaders[index];
      const value = normalizedRawHeaders[index + 1] ?? '';
      lines.push(`${name}: ${value}`);
    }

    return lines;
  }

  if (!headers) {
    return [];
  }

  const normalizedHeaders = normalizeHttpHeaders(headers);

  return Object.entries(normalizedHeaders).flatMap(([key, value]) =>
    Array.isArray(value)
      ? value.map((singleValue) => `${key}: ${singleValue}`)
      : [`${key}: ${value}`],
  );
}
